import React, { FC, useContext, PropsWithChildren } from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

import Menu from 'vibo-ui/Menu';
import Button from 'vibo-ui/Button';
import Dropdown from 'vibo-ui/Dropdown';
import WithEventRole from 'components/user/WithEventRole';
import StopPropagationWrapper from 'components/common/StopPropagationWrapper';
import SectionBlockInfo from 'components/sections/blocks/SectionBlock/SectionBlockInfo';
import { TimelineSongsContext } from '../TimelineSongsContext';

import { ADD_SONG_TO_SECTION, IMPORT_PLAYLIST_TO_SECTION_WEB } from 'graphql/mutations/songs';

import { useModal } from 'vibo-ui/Modal';
import { AllTimelineGqlOptions, useTimeline } from '../../TimelineContext';
import { useEvent } from 'components/context/EventContext';
import { getSectionSongsInfo } from 'services/sections/helpers';
import { isSingleSongSectionWithSong } from 'services/songs/helpers';

import { IconmoonFont } from 'vibo-ui/Icon';
import { EventUserType, Modals, MusicImportSource } from 'types/enums';
import { ConfirmActionModalProps } from 'components/modals/ConfirmActionModal';
import { AddSongFromLinkModalProps } from 'components/modals/AddSongFromLinkModal';
import { SectionSongIdeasModalProps } from 'components/modals/SectionSongIdeasModal';
import { AddSongsFromMusicSourceModalProps } from 'components/modals/AddSongsFromMusicSourceModal';
import { AddSongsToSectionFromSearchModalProps } from 'components/modals/AddSongsToSectionFromSearchModal';

import useStyles from './style';

const SongsBlockHeader: FC<PropsWithChildren> = ({ children }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { songsOptions, section, refetchSongs } = useContext(TimelineSongsContext);
  const {
    variables,
    playlists: { mutations, queries },
  } = useTimeline<AllTimelineGqlOptions>();

  const eventContext = useEvent();
  const { isEventGuest, canAddSong = false } = eventContext;

  const { openModal } = useModal();

  const openSectionSongIdeasModal = () =>
    openModal<SectionSongIdeasModalProps>({
      key: Modals.sectionSongIdeas,
      props: {
        variables,
        section,
        queries,
        mutations,
        eventContext,
      },
    });

  const openAddSongToSectionFromSearchModal = () => {
    openModal<AddSongsToSectionFromSearchModalProps>({
      key: Modals.addSongsToSectionFromSearch,
      props: {
        section,
        // @ts-ignore
        variables,
      },
    });
  };

  const openReplaceSongModal = (handleReplaceSong: Function) => {
    const nameToReplace = get(section, 'songsInfo', section.name);

    openModal<ConfirmActionModalProps>({
      key: Modals.confirmAction,
      props: {
        title: t('replaceSong'),
        children: t('replaceObjectSong', { object: nameToReplace }),
        submit: {
          text: t('replace'),
          onClick: () => handleReplaceSong(),
        },
      },
    });
  };

  const openAddSongsFromMusicSourceModal = (source: MusicImportSource) =>
    openModal<AddSongsFromMusicSourceModalProps>({
      key: Modals.addSongsFromMusicSource,
      props: {
        source,
        variables,
        importMutation: IMPORT_PLAYLIST_TO_SECTION_WEB,
        refetchPlaylist: refetchSongs,
        refetchQueries: ['sections', 'getSectionSongsStats', 'getSectionSongs'],
        shouldConfirmReplace: isSingleSongSectionWithSong(section),
        setIsConfirmReplaceVisible: openReplaceSongModal,
      },
    });
  const openAddSongFromLinkModal = () =>
    openModal<AddSongFromLinkModalProps>({
      key: Modals.addSongFromLink,
      props: {
        mutation: ADD_SONG_TO_SECTION,
        variables,
        refetchPlaylist: refetchSongs,
        refetchQueries: ['sections', 'getSectionSongsStats', 'getSectionSongs'],
        shouldConfirmReplace: isSingleSongSectionWithSong(section),
        setIsConfirmReplaceVisible: openReplaceSongModal,
      },
    });

  return (
    <div className={classNames('songsBlockHeader', classes.songsBlockHeader)}>
      {children}
      <SectionBlockInfo
        title={t('songs')}
        info={getSectionSongsInfo(section, songsOptions.sectionSongsData.totalCount)}
      />
      <WithEventRole roles={[EventUserType.dj]}>
        <Button
          onClick={e => {
            e.stopPropagation();
            openSectionSongIdeasModal();
          }}
          prefixIcon={IconmoonFont['lightBulbOff-16']}
          shape="round"
          size="lg"
          data-gtm-target-id="timeline-section-details-songs-songideas"
        >
          {t('songIdeasAmount', { count: section.songIdeasCount })}
        </Button>
      </WithEventRole>
      {canAddSong ? (
        <>
          <WithEventRole roles={[EventUserType.guest, EventUserType.host]}>
            <Button
              onClick={e => {
                e.stopPropagation();
                openAddSongToSectionFromSearchModal();
              }}
              prefixIcon={IconmoonFont['lightBulbOff-16']}
              shape="round"
              size="lg"
            >
              {t('songIdeas')}
            </Button>
          </WithEventRole>
          <StopPropagationWrapper className="addSongsWrapper">
            <Dropdown
              overlay={
                <Menu selectable={false}>
                  <Menu.Item
                    onClick={openAddSongToSectionFromSearchModal}
                    icon={IconmoonFont['search-16']}
                    key="search"
                    data-gtm-target-id={'timeline-section-details-songs-add-song-search'}
                  >
                    Search songs
                  </Menu.Item>
                  {isEventGuest ? null : (
                    <>
                      <Menu.Item
                        onClick={() => openAddSongsFromMusicSourceModal(MusicImportSource.spotify)}
                        icon={IconmoonFont['spotifyColorful-16']}
                        key="spotify"
                        data-gtm-target-id={'timeline-section-details-songs-add-song-spotify'}
                      >
                        {t('spotify')}
                      </Menu.Item>
                      <Menu.Item
                        onClick={() =>
                          openAddSongsFromMusicSourceModal(MusicImportSource.appleMusic)
                        }
                        icon={IconmoonFont['appleMusicColoful-16']}
                        key="appleMusic"
                        data-gtm-target-id={'timeline-section-details-songs-add-song-apple'}
                      >
                        {t('appleMusic')}
                      </Menu.Item>
                    </>
                  )}
                  <Menu.Item
                    onClick={openAddSongFromLinkModal}
                    icon={IconmoonFont['link-16']}
                    key="link"
                    data-gtm-target-id={'timeline-section-details-songs-add-song-link'}
                  >
                    {t('songLink')}
                  </Menu.Item>
                </Menu>
              }
            >
              <Button
                prefixIcon={IconmoonFont['plus-16']}
                displayType="bordered"
                shape="round"
                size="lg"
              >
                {t('addSongs')}
              </Button>
            </Dropdown>
          </StopPropagationWrapper>
        </>
      ) : null}
    </div>
  );
};

export default SongsBlockHeader;
